import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Resolve} from '@angular/router';
import {filter, Observable, of} from 'rxjs';
import {catchError, map, share, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {Hex} from 'viem';
import {AppConfigService} from '../../services/config.service';
import {destroy$} from '../destroyable';
import {DynWallet} from '../dynamic-xyz/dyn-xyz-wc/dyn-remote';
import {DynamicZyxDaemonService} from '../dynamic-xyz/dynamic-zyx-daemon.service';
import {isDynamicEmbeddedWallet} from '../dynamic-xyz/utils/dyn-xyz-utils';

@Injectable()
export class DexOnboardingFundingResolverService implements Resolve<void> {

    private readonly appConfigService = inject(AppConfigService);
    private readonly httpClient = inject(HttpClient);
    private readonly dynamicZyxDaemonService = inject(DynamicZyxDaemonService);

    private isInitialized: boolean = false;
    private req$: Observable<void> | undefined = undefined;

    private readonly destroy$ = destroy$();

    public resolve(): Observable<void> {
        let result: Observable<void> = of(undefined);

        if (!this.isInitialized) {
            this.req$ ??= this.sendReq().pipe(
                tap(() => {
                    this.isInitialized = true;
                    this.req$ = undefined;
                }),
                share()
            );

            result = this.req$;
        }

        return result;
    }

    private sendReq(): Observable<void> {
        let result: Observable<void> = of(undefined);

        const url = this.appConfigService.api['testTokenAllocation'];
        const dynRemote = this.dynamicZyxDaemonService.dynRemote;

        if (!url || !dynRemote) {
            return result;
        }

        if (url) {
            result = this.getWallet().pipe(
                switchMap(wallet => {
                    if (!wallet) {
                        return of(undefined);
                    }

                    const reqBody: {
                        to: Hex;
                    } = {
                        to: wallet.address as Hex
                    };

                    return this.httpClient.post<unknown>(url, reqBody).pipe(
                        catchError(() => of(undefined)),
                        map<unknown, undefined>(() => undefined),
                        takeUntil(this.destroy$)
                    );
                })
            );
        }

        return result;
    }

    private getWallet(): Observable<DynWallet | undefined> {
        const dynRemote = this.dynamicZyxDaemonService.dynRemote;
        const primaryWallet = dynRemote.primaryWallet$.value;

        const isWalletMatch = (w?: DynWallet) =>
            w && isDynamicEmbeddedWallet(w, dynRemote);

        let result: Observable<DynWallet | undefined> = of(undefined);

        if (isWalletMatch(primaryWallet)) {
            result = of(primaryWallet);
        } else if (primaryWallet) {
            result = (dynRemote.userWallets$.value?.length)
                ? of(dynRemote.userWallets$.value.find(isWalletMatch))
                : dynRemote.userWallets$.pipe(
                    filter(ws => !!ws?.length),
                    take(1),
                    map(ws => ws.find(isWalletMatch))
                );
        }

        return result;
    }

}
